/**
 * CMS SDK - ContentCard 组件
 * 内容卡片展示
 */

import React from 'react';
import { t } from '../i18n';
import { ContentStatus } from '../types';

// 状态颜色
const statusColors = {
  [ContentStatus.DRAFT]: '#6b7280',
  [ContentStatus.PENDING]: '#f59e0b',
  [ContentStatus.APPROVED]: '#10b981',
  [ContentStatus.REJECTED]: '#ef4444',
  [ContentStatus.ARCHIVED]: '#6b7280',
  [ContentStatus.SCHEDULED]: '#8b5cf6',
};

/**
 * 内容卡片组件
 * @param {Object} props - 组件属性
 */
export function ContentCard({
  content,
  onClick,
  onEdit,
  onDelete,
  showStatus = true,
  showStats = true,
  showAuthor = true,
  className = '',
}) {
  // 格式化日期
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return '刚刚';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} 分钟前`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} 小时前`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} 天前`;
    return date.toLocaleDateString();
  };

  // 格式化数字
  const formatNumber = (num) => {
    if (num >= 10000) return (num / 10000).toFixed(1) + 'w';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num?.toString() || '0';
  };

  return (
    <div 
      className={`cms-content-card ${className}`}
      onClick={() => onClick?.(content)}
    >
      {/* 封面图 */}
      {content.cover_url && (
        <div className="cms-card-cover">
          <img src={content.cover_url} alt={content.title} />
          {showStatus && content.status !== ContentStatus.APPROVED && (
            <span 
              className="cms-card-status"
              style={{ backgroundColor: statusColors[content.status] }}
            >
              {t(`status${content.status.charAt(0).toUpperCase() + content.status.slice(1)}`)}
            </span>
          )}
        </div>
      )}

      {/* 内容区 */}
      <div className="cms-card-body">
        {/* 分类标签 */}
        {content.category && (
          <span className="cms-card-category">{content.category.name}</span>
        )}

        {/* 标题 */}
        <h3 className="cms-card-title">{content.title}</h3>

        {/* 摘要 */}
        {content.summary && (
          <p className="cms-card-summary">{content.summary}</p>
        )}

        {/* 标签 */}
        {content.tags?.length > 0 && (
          <div className="cms-card-tags">
            {content.tags.slice(0, 3).map((tag, i) => (
              <span 
                key={tag.id || i} 
                className="cms-card-tag"
                style={{ backgroundColor: tag.color ? `${tag.color}20` : undefined }}
              >
                {tag.name || tag}
              </span>
            ))}
            {content.tags.length > 3 && (
              <span className="cms-card-tag-more">+{content.tags.length - 3}</span>
            )}
          </div>
        )}
      </div>

      {/* 底部 */}
      <div className="cms-card-footer">
        {/* 作者信息 */}
        {showAuthor && (
          <div className="cms-card-author">
            {content.author_avatar && (
              <img 
                src={content.author_avatar} 
                alt={content.author_name} 
                className="cms-card-avatar"
              />
            )}
            <span className="cms-card-author-name">
              {content.author_name || 'Anonymous'}
            </span>
            <span className="cms-card-date">
              {formatDate(content.published_at || content.created_at)}
            </span>
          </div>
        )}

        {/* 统计数据 */}
        {showStats && (
          <div className="cms-card-stats">
            <span className="cms-card-stat">
              <span className="cms-stat-icon">👁</span>
              {formatNumber(content.views)}
            </span>
            <span className="cms-card-stat">
              <span className="cms-stat-icon">❤️</span>
              {formatNumber(content.likes)}
            </span>
            <span className="cms-card-stat">
              <span className="cms-stat-icon">💬</span>
              {formatNumber(content.comments)}
            </span>
          </div>
        )}

        {/* 操作按钮 */}
        {(onEdit || onDelete) && (
          <div className="cms-card-actions">
            {onEdit && (
              <button 
                className="cms-card-action"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(content);
                }}
              >
                {t('edit')}
              </button>
            )}
            {onDelete && (
              <button 
                className="cms-card-action cms-card-action-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(content);
                }}
              >
                {t('delete')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ContentCard;
